<template>
  <div>
    <div v-if="isLoadingTraining && !training" class="tab">
      <div class="tab__header">
        <div class="tab__title h2">Загрузка тренировки…</div>
      </div>
    </div>

    <div v-else-if="loadError" class="tab">
      <div class="tab__header">
        <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="come-back">
          К марафону
        </router-link>
        <div class="tab__title h2">Ошибка загрузки</div>
      </div>
      <div class="tab__content">
        <p>Не удалось загрузить тренировку. Попробуйте вернуться и запустить снова.</p>
        <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="btn btn-green">
          К марафону
        </router-link>
      </div>
    </div>

    <template v-else-if="training">
      <div class="tab">
        <div class="tab__header">
          <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="come-back">
            К марафону
          </router-link>
          <div class="tab__title h2">Тренировка — {{ training.categoryName }}</div>
        </div>

        <div v-if="training.questions.length === 0" class="tab__content training-empty">
          <div class="h3">Нет вопросов для тренировки</div>
          <p>В этой сессии пока нет вопросов с ошибками. Продолжайте марафон!</p>
          <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="btn btn-green">
            Продолжить марафон
          </router-link>
        </div>

        <template v-else>
          <div class="training-progress-row">
            <div class="training-progress-label">
              {{ trainingIsFinished ? training.questions.length : training.pos + 1 }}
              из {{ training.questions.length }}
            </div>
            <div class="marathon-stats__bar">
              <div
                class="marathon-stats__fill marathon-stats__fill--green"
                :style="{ width: progressPercent + '%' }"
              ></div>
            </div>
          </div>

          <div v-if="trainingIsFinished" class="tab__content training-completed">
            <div class="h3">Тренировка завершена!</div>
            <p>Вы прошли {{ training.questions.length }} вопросов.</p>
            <router-link :to="{ name: 'marathon-session', params: { uuid } }" class="btn btn-green">
              Продолжить марафон
            </router-link>
          </div>

          <div v-else-if="trainingCurrentQuestion" class="tab__question">
            <div v-if="trainingCurrentQuestion.image" class="ticket-question__image">
              <img :src="`${$APP_URL}/storage/${trainingCurrentQuestion.image}`" class="mb-3 w-100" />
            </div>

            <div class="ticket-question">
              <div class="ticket-number">Вопрос #{{ trainingCurrentQuestion.id }}</div>
              <div class="h3" v-html="trainingCurrentQuestion.text"></div>
            </div>

            <form @submit.prevent>
              <div
                v-for="(answerText, key, index) in trainingCurrentQuestion.answers"
                :key="key"
                class="checkout-group"
                :class="getAnswerClass(key)"
              >
                <input
                  type="radio"
                  name="training-answer"
                  :id="'training-answer-' + key"
                  class="checkout-group__input"
                  :disabled="isAnswered || isLoadingAnswer"
                  @click="selectAnswer(key)"
                />
                <label :for="'training-answer-' + key" class="checkout-group__label">
                  <span>{{ index + 1 }}</span>
                  <div class="checkout-group__text">{{ answerText }}</div>

                  <div v-if="isAnswered && shouldShowComment(key)" class="answer-info">
                    <hr />
                    <div class="text" v-html="lastAnswerResult.comment || ''"></div>
                  </div>
                </label>
              </div>

              <button
                v-if="isAnswered"
                type="button"
                class="btn btn-green"
                :disabled="isLoadingAnswer"
                @click="goToNextQuestion"
              >
                {{ isLastQuestion ? 'Завершить' : 'Далее' }}
              </button>
            </form>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'MarathonTraining',
    data() {
      return {
        loadError: false,
        pendingAnswerResult: null,
      };
    },
    computed: {
      ...mapGetters('marathon', [
        'training',
        'trainingCurrentQuestion',
        'trainingIsFinished',
        'lastAnswerResult',
        'isLoadingTraining',
        'isLoadingAnswer',
      ]),
      uuid() {
        return this.$route.params.uuid;
      },
      limitParam() {
        return this.$route.query.n || null;
      },
      onlyUnmastered() {
        return this.$route.query.mode !== 'all';
      },
      isAnswered() {
        return !!this.lastAnswerResult;
      },
      isLastQuestion() {
        if (!this.training) return false;
        return this.training.pos >= this.training.questions.length - 1;
      },
      progressPercent() {
        if (!this.training || !this.training.questions.length) return 0;
        return Math.round((this.training.pos / this.training.questions.length) * 100);
      },
    },
    methods: {
      ...mapActions('marathon', [
        'startTraining',
        'advanceTrainingQuestion',
        'clearTraining',
        'submitAnswer',
      ]),
      async fetchTraining() {
        this.loadError = false;
        try {
          await this.startTraining({ uuid: this.uuid, limit: this.limitParam, onlyUnmastered: this.onlyUnmastered });
        } catch (e) {
          this.loadError = true;
        }
      },
      getAnswerClass(key) {
        if (!this.isAnswered) return {};
        const isCorrect = key === this.lastAnswerResult.correct_answer;
        const isUserAnswer = key === this.lastAnswerResult.answer;
        return {
          'answer-right': isCorrect,
          'answer-wrong': isUserAnswer && !isCorrect,
        };
      },
      shouldShowComment(key) {
        if (!this.isAnswered) return false;
        return key === this.lastAnswerResult.correct_answer
          && this.lastAnswerResult.answer !== this.lastAnswerResult.correct_answer;
      },
      async selectAnswer(answerKey) {
        if (this.isAnswered || this.isLoadingAnswer) return;
        try {
          const resp = await this.submitAnswer({
            uuid: this.uuid,
            questionId: this.trainingCurrentQuestion.id,
            answer: answerKey,
          });

          if (resp && resp.is_correct && !this.isLastQuestion) {
            setTimeout(() => {
              if (this.isAnswered) {
                this.goToNextQuestion();
              }
            }, 700);
          }
        } catch (e) {
          // ошибки обрабатываются через store
        }
      },
      goToNextQuestion() {
        this.advanceTrainingQuestion();
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      },
    },
    created() {
      this.fetchTraining();
    },
    beforeDestroy() {
      this.clearTraining();
    },
  };
</script>

<style scoped>
.training-progress-row {
  margin: 16px 0 24px;
  padding-right: 26px;
}
.training-progress-label {
  font-size: 13px;
  color: #555;
  margin-bottom: 4px;
}
.marathon-stats__bar {
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}
.marathon-stats__fill {
  height: 100%;
  transition: width 0.3s ease;
}
.marathon-stats__fill--green {
  background: #4caf50;
}
.training-completed {
  text-align: center;
  padding: 32px 16px;
}
.training-completed .btn {
  margin-top: 16px;
}
.training-empty {
  text-align: center;
  padding: 32px 16px;
}
.training-empty .btn {
  margin-top: 16px;
}
</style>
